'use client';
import { Spinner } from '@common/components';
import { useSuspenseQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { UnauthQueryOptions } from '@/api';
import { isSecondDecemberEvent } from '@/app/donation/event/market/invite/_utils/isSecondDecemberEvent';

import { INVITE_CONFIG } from '../../constants';

interface RecentWinnerData {
  maskedNickname: string;
  rewardPoint: number;
}

export const RecentWinnersSection = () => {
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const { data: recentWinnersData, isLoading } = useSuspenseQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home-donation-event-history/reward/recent'
    ]({
      query: {
        eventCodeList: isSecondDecemberEvent()
          ? ['INVITEE_ON_JOIN_2025_12_2ND', 'INVITER_ON_DONATION_2025_12_2ND']
          : ['INVITEE_ON_JOIN_2025_12_1ND', 'INVITER_ON_DONATION_2025_12_1ND'],
        size: 3,
      },
    }),
    select: ({ context }) =>
      context?.contents ?? [] as RecentWinnerData[],
  });

  const hasWinnersData = (recentWinnersData?.length ?? 0) > 0;

  useEffect(() => {
    if (!hasWinnersData) return;

    const carouselInterval = setInterval(() => {
      setCurrentCarouselIndex(
        (prevIndex) => (prevIndex + 1) % recentWinnersData.length,
      );
    }, INVITE_CONFIG.animation.carouselRotationIntervalMs);

    return () => clearInterval(carouselInterval);
  }, [recentWinnersData.length, hasWinnersData]);

  if (isLoading) {
    return (
      <div className="w-full rounded-xsmall bg-Gray-25 py-100 flex-center">
        <Spinner size={16} />
      </div>
    );
  }

  if (!hasWinnersData) return null;

  const currentWinnerMessage = recentWinnersData[currentCarouselIndex];
  const nextWinnerMessage =
    recentWinnersData[(currentCarouselIndex + 1) % recentWinnersData.length];
  const secondNextWinnerMessage =
    recentWinnersData[(currentCarouselIndex + 2) % recentWinnersData.length];

  return (
    <div className="flex w-full flex-col items-center">
      <QuoteIcon />
      <div
        className="relative h-146 w-full overflow-hidden"
        style={{
          WebkitMask:
            'linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
          mask: 'linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
        }}
      >
        <motion.div
          key={`carousel-container-${currentCarouselIndex}`}
          initial={{ y: 0 }}
          animate={{ y: -64 }}
          transition={{
            duration: INVITE_CONFIG.animation.transitionDurationSeconds,
            ease: INVITE_CONFIG.animation.easingCurve,
          }}
          className="flex flex-col gap-12"
        >
          <WinnerMessage
            winner={currentWinnerMessage}
            opacity={1}
            animateOpacity={0.2}
          />
          <WinnerMessage
            winner={nextWinnerMessage}
            opacity={0.2}
            animateOpacity={1}
          />
          <WinnerMessage
            winner={secondNextWinnerMessage}
            opacity={0.2}
            animateOpacity={0.2}
          />
        </motion.div>
      </div>
    </div>
  );
};

const QuoteIcon = () => (
  <div className="size-20 flex-center">
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path
        d="M8.33333 25C8.33333 20.3977 11.731 16.6667 16 16.6667V13.3333C9.81444 13.3333 5 18.1478 5 24.3333V31.6667H16.6667V20H8.33333V25ZM25 25C25 20.3977 28.3977 16.6667 32.6667 16.6667V13.3333C26.4811 13.3333 21.6667 18.1478 21.6667 24.3333V31.6667H33.3333V20H25V25Z"
        fill="#ffe560"
      />
    </svg>
  </div>
);

interface WinnerMessageProps {
  winner: RecentWinnerData;
  opacity: number;
  animateOpacity: number;
}

const WinnerMessage = ({
  winner,
  opacity,
  animateOpacity,
}: WinnerMessageProps) => {
  const winnerMessageText = `${winner.maskedNickname}님이\n${winner.rewardPoint.toLocaleString()}원을 받았어요!`;

  return (
    <motion.div
      initial={{ opacity }}
      animate={{ opacity: animateOpacity }}
      transition={{
        duration: INVITE_CONFIG.animation.transitionDurationSeconds,
        ease: 'easeOut',
      }}
      className="whitespace-pre-line text-center font-700 text-Y-300 typo-body1"
    >
      {winnerMessageText}
    </motion.div>
  );
};
